/**
 * Tema de exportação — resolve os tokens canônicos do DS (tokens.css) para
 * valores concretos que as camadas de exportação entendem (jsPDF, canvas 2D
 * e html2canvas não resolvem var()).
 *
 * Usado pelos DOIS caminhos de exportação:
 *  - Modal manual (components/Exportacao) — resolve no clique de "Exportar".
 *  - Agendador (AgendamentoContext → executor) — resolve no momento da
 *    execução, na aba principal. O iframe oculto herda o mesmo localStorage,
 *    logo [data-theme]/[data-accent] são idênticos nos dois documents.
 *
 * O resultado acompanha o tema (light/dark), a cor de accent escolhida pelo
 * usuário ([data-accent]) e a densidade de tabelas das Configurações.
 */

import { resolverCorCss, resolverCorCssSobreFundo } from "./resolverCorCss";

// Mantido em sincronia com STORAGE_KEY do AuthContext.jsx (ver tenantStorage).
const CHAVE_SESSAO = "pharuerp:access-session";

/**
 * Assinatura de toda exportação: o produto e a empresa de quem exporta.
 *
 * Lida da sessão persistida (e não do React) porque o executor do agendador
 * roda fora da árvore de componentes. Sem unidade conhecida, fica só o
 * produto: nunca o nome fixo de um cliente.
 */
export function marcaDaExportacao() {
  let nome = "";
  try {
    const sessao = JSON.parse(window.localStorage.getItem(CHAVE_SESSAO) || "null");
    nome = String(sessao?.usuario?.business_unit_nome || "").trim();
  } catch {
    nome = "";
  }
  return nome ? `PharuERP — ${nome}` : "PharuERP";
}

// Espelha --font-body de tokens.css — fallback quando não há DOM.
export const FONTE_EXPORT_PADRAO =
  '"Sora", "Segoe UI", system-ui, sans-serif';

const CHAVE_DENSIDADE = "pharuerp:density";
const DENSIDADES_VALIDAS = ["compact", "cozy", "comfort"];

/**
 * Densidade atual das tabelas (Configurações → Aparência). Lê direto do
 * localStorage para funcionar também fora do React (executor do agendador).
 */
export function lerDensidadeAtual() {
  try {
    const valor = window.localStorage.getItem(CHAVE_DENSIDADE);
    return DENSIDADES_VALIDAS.includes(valor) ? valor : "cozy";
  } catch {
    return "cozy";
  }
}

function lerFonteBody() {
  try {
    const fonte = getComputedStyle(document.body).fontFamily;
    return fonte && fonte.trim() ? fonte : FONTE_EXPORT_PADRAO;
  } catch {
    return FONTE_EXPORT_PADRAO;
  }
}

// Fallbacks dos tons semânticos = tema claro de tokens.css.
const TONS_FALLBACK = {
  info: { bg: "#EBF1FE", text: "#1E429F" },
  success: { bg: "#E3F5EA", text: "#166534" },
  warning: { bg: "#FEF3D9", text: "#854D0E" },
  danger: { bg: "#FDE6E4", text: "#991B1B" },
  neutral: { bg: "#EEF0F4", text: "#3D4554" },
};

/**
 * Resolve o tema corrente ([data-theme] + [data-accent] no <html>) para o
 * shape hex consumido por gerarPDF/gerarBlobImagem/combineCanvasesVertical.
 * Os fallbacks espelham o tema claro padrão (mesma degradação de antes).
 */
export function montarTemaExportAtual() {
  const background = resolverCorCss("var(--bg)", "#ffffff");
  // Tons semânticos (pills de status, barras de estado do PDF). No dark os
  // tokens *-bg são rgba — compomos sobre o fundo para virar hex sólido
  // (jsPDF não tem alpha).
  const tones = {};
  for (const [tom, fb] of Object.entries(TONS_FALLBACK)) {
    tones[tom] = {
      bg: resolverCorCssSobreFundo(`var(--${tom}-bg)`, background, fb.bg),
      text: resolverCorCss(`var(--${tom}-text)`, fb.text),
    };
  }
  return {
    background,
    textPrimary: resolverCorCss("var(--ink)", "#16181e"),
    textSecondary: resolverCorCss("var(--ink-2)", "#686d76"),
    textMuted: resolverCorCss("var(--ink-3)", "#949aa0"),
    border: resolverCorCss("var(--border)", "#e4e6ea"),
    cardBg: resolverCorCss("var(--surface-2)", "#f9fafc"),
    rowAlt: resolverCorCss("var(--surface)", "#fafbfd"),
    // Accent do usuário (violet/ocean/emerald/sunset/chart1-8) — antes era
    // um vermelho fixo #ec3223 que ignorava a escolha em Configurações.
    // Fallback acompanha o default novo (Por do Sol, 2026-08-04) — so vale
    // quando a var CSS nao resolve (ex.: render fora do DOM principal).
    accent: resolverCorCss("var(--accent)", "#F97316"),
    inkOnAccent: resolverCorCss("var(--ink-on-accent)", "#ffffff"),
    fontFamily: lerFonteBody(),
    tones,
  };
}
